//This is a template for the deeper look page

import { useState } from "react";
import DeeperLookIcon_Trans from "../../../assets/UI/DeeperLookIcon_Trans.png";
import HeaderNavNTM from "../../../Components/Header/Header_Nav_NoBottomMargin/Header_Nav_NBM";
import "../DeeperLook.css"; 

export default function PronounsDLook() {
    //array for storing text input
    //defines type and content so they can be processed later
const categories = {

     Pronouns_DL_Overview: {
 
         title: "Overview",
         content:[
                { type: "subheader", content: "Introduction" },
                { type: "paragraph", content: "Pronouns are words that replace people or things in sentences, like “I,” “you,” “he,” or “they.” In Korean, pronouns are a little different from English because they change depending on formality and politeness. Choosing the correct pronoun is important to sound natural and respectful in conversations."},
                { type: "paragraph", content: "This deck will help you:" },
                { type: "list", items: 
                    [
                    "Recognize and use subject, object, possessive, and reflexive pronouns", 
                    "Understand formal vs. informal pronouns", 
                    "Practice using pronouns in everyday sentences"
                    ] 
                },
                {type: "paragraph", content: "Tip: In Korean, pronouns are often omitted when the context is clear, so learning how and when to use them makes your sentences sound smooth and natural."}

                ] 
    },
    SubjectPronouns: {
        title: "Subject Pronouns",
        content: [
                { type: "subheader", content: "Common Subject Pronouns" },
                { type: "paragraph", content: "Subject pronouns are used for the person or thing performing the action in a sentence. In Korean, pronouns change depending on formality and relationship." },
                { type: "subheader", content: "Common Subject Pronouns" },
                { 
                    type: "table", 
                    rows: [
                        ["English", "Korean (Formal)", "Korean (Informal)", "Notes"],
                        ["I", "저 (jeo)", "나 (na)", "Use 저 in polite speech, 나 with friends/family"],
                        ["You", "당신 (dang-sin)", "너 (neo)", "당신 is rarely used in conversation; 너 is informal"],
                        ["He", "그 (geu)", "–", "Gender-neutral in most cases; used like 'that person'"],
                        ["She", "그녀 (geu-nyeo)", "–", "Less commonly used in speech; usually context makes gender clear"],
                        ["We", "저희 (jeo-hui)", "우리 (uri)", "저희 is formal, 우리 is casual/friendly"],
                        ["They", "그들 (geu-deul)", "–", "Usually, context is enough; '그들' is formal/written"]
                    ]
                },
                { type: "subheader", content: "Examples" },
                { type: "paragraph", content: "저는 학생이에요. → I am a student. (formal)" },
                { type: "paragraph", content: "나는 학생이야. → I am a student. (informal)" },
                { type: "paragraph", content: "우리는 친구예요. → We are friends." },
                { type: "paragraph", content: "💡 Tip: In Korean, subject pronouns are often omitted when the context is clear. For example, instead of saying 'I am a student,' you can simply say 학생이에요." }
            ]
    },
    
    ObjectPronouns: {
        title: "Object Pronouns",
        content: [
            { type: "subheader", content: "Common Object Pronouns" },
            { type: "paragraph", content: "Object pronouns refer to the person or thing receiving the action in a sentence. In Korean, these pronouns often mirror the subject pronouns but are marked by context rather than strict grammatical changes, and politeness still matters." },
            { type: "subheader", content: "Common Object Pronouns" },
            { 
                type: "table", 
                rows: [
                    ["English", "Korean (Formal)", "Korean (Informal)", "Notes"],
                    ["Me", "저를 (jeo-reul)", "나를 (na-reul)", "-를/-을 is the object particle"],
                    ["You", "당신을 (dang-sin-eul)", "너를 (neo-reul)", "당신 is formal but rarely used; 너 is informal"],
                    ["Him", "그를 (geu-reul)", "–", "Object form uses -를/-을"],
                    ["Her", "그녀를 (geu-nyeo-reul)", "–", "Less common in spoken Korean"],
                    ["Us", "저희를 (jeo-hui-reul)", "우리를 (uri-reul)", "-를/-을 for object"],
                    ["Them", "그들을 (geu-deul-eul)", "–", "Often context is enough in speech"]
                ]
            },
            { type: "subheader", content: "Examples" },
            { type: "paragraph", content: "선생님이 저를 도와주셨어요. → The teacher helped me." },
            { type: "paragraph", content: "나는 너를 기다릴게. → I will wait for you. (informal)" },
            { type: "paragraph", content: "우리는 그를 만났어요. → We met him." },
            { type: "subheader", content: "Try It Yourself!" },
            { type: "paragraph", content: "Always add the object particle -을/-를 after the pronoun when it is the object of the verb. For example:\n\n나 → 나를\n\n저 → 저를" }
    ]
    },

    PossessivePronouns: {
        title: "Possessive Pronouns",
        content: [
            { type: "subheader", content: "Using Possessive Pronouns" },
            { type: "paragraph", content: "Possessive pronouns show ownership or belonging, like 'my,' 'your,' or 'their.' In Korean, possession is expressed by adding -의 (ui) after the pronoun, which works like '’s' in English." },
            { type: "subheader", content: "Common Possessive Pronouns" },
            { 
                type: "table", 
                rows: [
                    ["English", "Korean (Formal)", "Korean (Informal)", "Example"],
                    ["My / Mine", "제 (je) + noun", "내 (nae) + noun", "제 책 → my book / 내 책 → my book (informal)"],
                    ["Your / Yours", "당신의 (dangsin-ui)", "네 / 니 (ne / ni) + noun", "당신의 이름 → your name / 네 이름 → your name (informal)"],
                    ["His / Her / Hers", "그의 / 그녀의", "–", "그의 집 → his house / 그녀의 가방 → her bag"],
                    ["Our / Ours", "저희의 / 우리", "–", "저희의 계획 → our plan / 우리 집 → our house"],
                    ["Their / Theirs", "그들의", "–", "그들의 친구 → their friend"]
                ]
            },
            { type: "subheader", content: "Examples" },
            { type: "paragraph", content: "제 이름은 민지예요. → My name is Minji." },
            { type: "paragraph", content: "내 가방이 여기 있어요. → My bag is here. (informal)" },
            { type: "paragraph", content: "그녀의 고양이가 귀여워요. → Her cat is cute." },
            { type: "subheader", content: "Try It Yourself!" },
            { type: "paragraph", content: "In everyday speech, Koreans often omit -의 with informal pronouns:\n\n우리 집 → our house (instead of 우리의 집)\n\n내 친구 → my friend" }
        ]
    },

    ReflexivePronouns: {
        title: "Reflexive Pronouns",
        content: [
            { type: "subheader", content: "Overview of Reflexive Pronouns" },
            { type: "paragraph", content: "Reflexive pronouns refer back to the subject of the sentence, like 'myself,' 'yourself,' or 'themselves.' In Korean, reflexive pronouns are less common than in English, and often the meaning is clear from context, but there are still ways to express them." },
            { type: "subheader", content: "Common Reflexive Pronouns" },
            { 
                type: "table", 
                rows: [
                    ["English", "Korean", "Notes / Examples"],
                    ["Myself", "저 자신 (jeo ja-sin) / 나 자신 (na ja-sin)", "Formal / informal"],
                    ["Yourself", "당신 자신 (dangsin ja-sin) / 너 자신 (neo ja-sin)", "Politeness applies"],
                    ["Himself / Herself", "그 자신 / 그녀 자신", "Often just context is enough"],
                    ["Ourselves", "저희 자신 / 우리 자신", "–"],
                    ["Themselves", "그들 자신", "–"]
                ]
            },
            { type: "subheader", content: "Examples" },
            { type: "paragraph", content: "저는 저 자신을 믿어요. → I believe in myself." },
            { type: "paragraph", content: "너 자신을 자랑스러워해. → Be proud of yourself. (informal)" },
            { type: "paragraph", content: "그들은 그들 자신을 도왔어요. → They helped themselves." },
            { type: "subheader", content: "Try It Yourself!" },
            { type: "paragraph", content: "Reflexive pronouns are often optional. If the subject is clear, Koreans might just say:\n\n나는 믿어요. → I believe. (context implies 'myself')" }
        ]
    },
    
    PolitenessLevels: {
        title: "Politeness Levels & Usage Notes",
        content: [
            { type: "subheader", content: "Overview of Politeness Levels" },
            { type: "paragraph", content: "Korean pronouns are closely tied to politeness and social hierarchy. Choosing the right pronoun helps you sound natural and respectful." },
            { type: "subheader", content: "Key Points" },
            { type: "list", items: [
                "Formal pronouns are used in polite situations, with strangers, elders, or in professional settings. Example: 저 (I), 저희 (we)",
                "Informal pronouns are used with close friends, family, or people younger than you. Example: 나 (I), 우리 (we)",
                "Some pronouns like 당신 (you) are rarely used in conversation because they can sound distant or confrontational. Context is often preferred instead of saying 'you.'",
                "Omitting pronouns is very common in Korean. If the subject or object is clear, it’s natural to leave them out. Example: '먹었어요?' → Did (you) eat? (subject understood from context)"
            ]},
            { type: "subheader", content: "Try It Yourself!" },
            { type: "paragraph", content: "Focus on context and relationships. Native speakers often rely on context instead of pronouns, so learning when to use them versus leaving them out is essential for sounding natural." }
        ]
    },

    CommonPhrases: {
        title: "Common Phrases with Pronouns",
        content: [
            { type: "subheader", content: "Overview" },
            { type: "paragraph", content: "Using pronouns in everyday phrases helps you sound natural and polite in conversation." },
            { type: "subheader", content: "Examples with Subject Pronouns" },
            { type: "paragraph", content: "저는 학생이에요. → I am a student. (formal)" },
            { type: "paragraph", content: "나는 피자를 좋아해. → I like pizza. (informal)" },
            { type: "paragraph", content: "우리는 친구예요. → We are friends." },
            { type: "subheader", content: "Examples with Object Pronouns" },
            { type: "paragraph", content: "선생님이 저를 도와주셨어요. → The teacher helped me." },
            { type: "paragraph", content: "나는 너를 기다릴게. → I will wait for you. (informal)" },
            { type: "subheader", content: "Examples with Possessive Pronouns" },
            { type: "paragraph", content: "제 책이에요. → It’s my book. (formal)" },
            { type: "paragraph", content: "내 집은 커요. → My house is big. (informal)" },
            { type: "paragraph", content: "우리 계획을 바꿨어요. → We changed our plan." },
            { type: "subheader", content: "Examples with Reflexive Pronouns" },
            { type: "paragraph", content: "저는 저 자신을 믿어요. → I believe in myself." },
            { type: "paragraph", content: "너 자신을 자랑스러워해. → Be proud of yourself. (informal)" },
            { type: "paragraph", content: "💡 Tip: Try replacing nouns with pronouns in simple sentences to practice smoothly. For example:\n\n“민지 책이에요” → “제 책이에요”" }
        ]
    }
 }
 
 
   //Make overveiw the default catgory selected when page loads
   const [selectedCategory, setSelectedCategory] = useState("Pronouns_DL_Overview");
 
 
   return (
     <div>
       
       <HeaderNavNTM GoBack_to="/PronounsNav"/>
 
       <div className="blue-header-dlook">
         <img
           className="DeeperLookImage-dlook"
           src={DeeperLookIcon_Trans}
           alt="Deeper Look"
         />
       </div>
       {/* End of blue header */}
 
 
 
 
       {/* Body */}
       <div className="panel-container-dlook">
         
         
         
         {/* Side panel */}
         <div className="side-panel-dlook">
           {Object.keys(categories).map(key => (
             //iterates over the properties (keys) of an object to produce a new array
 
             //Object.keys() --> Static method, takes an object (i.e. categories)
                 //as the argument
             //returns new array containning all "string-keyed property names"
                 //of the object
 
             //EXAMPLE
             /*
 
                 const categories = {
                     fruits: 'apple',
                     vegetables: 'carrot',
                     dairy: 'milk'
                     };
                 
                 Object.keys(categories) will return ['fruits', 'vegetables', 'dairy']
 
             */
 
             //this function gives an array of strings
                 //ex: ["Overview", "Subject1", "Subject2"...]
 
 
             //--Map Function--//
                 //It creates a new array containing the values returned by the function for each element.
                 //inside the parentheses (...) of the arrow function, 
                     //you can place code that will run for each key.   
                 //The category variable is a parameter for the arrow function. 
                     //During each iteration, it holds the current array element, which is the string 
                     //key (e.g., 'fruits').
             
             <p
               key={key}
                 //key is a special attribute required by React when rending a list of elements
                     //helps id and track which items changed
                     //value must be unquie and stable
                     //Object.keys() produces list of unique catagory names--> able to be a key
               className={key === selectedCategory 
                 ? "category-active" 
                 : "category-inactive"}
               
                 //Conditional expression, dynamically sets CSS class for each p element
                 //? "category-active" : "category-inactive"
                     //Ternary operator
                         //if comparison is true, className is set to "category-active"
                             //used to highlight selected category
                         //if comparison is flase, className is set to "category-inactive"
                             //will be default style for all non-selected categories
               
             
               onClick={() => setSelectedCategory(key)}
                 //Event handler, responds to user input
                 //OnClick ==> React event handler, listens for click event on p element
                     //calls state-updating function 'setSelectedCategory' from useState hook
                     //passes name of current category in 'category' as new value for selectedCatgory state
                         //rerenders component, updates className and visual appearence of categories            
             
             >
 
                     
               {categories[key].title}
             </p>
           ))}
         </div>
 
         {/* Text panel */}
     <div className="text-panel-dlook">
        {(categories[selectedCategory]?.content ?? []).map((item, index) => {
   // your switch logic here
 
            
            //(categories[selectedCategory] ?? [])
                 //ccesses the catgories object using the variable 'selectedCategory' as the key
                 //retreives specific array of cintent items that belongs to the currently active category
                 // ?? []
                     //?? --> nullish coalescing operator
                         //provides fallback
                         //if categories[selectedCategory] is null or undefined (no category selected), then
                             //defaults to an empty array []
                             //this prevents application from crashing + ensures map method is called safely
 
         //.map((item, index) => ... ): 
              //array method iterates over each item in the array, returns a new array of JSX elements.
 
            switch (item.type) {
                 //switch statement checks the value of item.type for each item
                     //allows components to render different html elements based on the data's type
            
            //if item.type is "subheader", return <h> element
             case "subheader":
                 return <h3 key={index} className="subheader-dlook">{item.content}</h3>;
                 //key={index}--> unquie key prop assigned to help react efficeintly update list of elements
 
             case "paragraph":
                 return <p key={index}>{item.content}</p>;
                 //{item.content}--> displays text content for the 'paragraph'
 
             case "list":
                 return (
                 <ul key={index} className="list-dlook">
                     {item.items.map((bullet, i) => (
                     <li key={i}>{bullet}</li>
                     ))}
                 </ul>
                 );
             //<ul> --> unordered list html element
 
             case "table":
                 return (
                 <table key={index} className="table-dlook">
                     <tbody>
                     {item.rows.map((row, r) => (
                         <tr key={r}>
                         {row.map((cell, c) => (
                             <td key={c}>{cell}</td>
                         ))}
                         </tr>
                     ))}
                     </tbody>
                 </table>
                 );
                 //{item.rows.map((row, r) => ... )}
                     //iterates over the rows property of the current item object
                     //for each element in the rows array, it return a <tr> (table row) element
                     //key={r}--> provides unique key for each row
                 //{row.map((cell, c) => ... )}
                     //executed for every row and iterates over the array of cell data within that row.
                     //for each element, returns <td> (table data) element -->cell in the table
 
             default:
                 return null;
             //handles any item.type that doesnt match defined case
             //return null tells react to render nothing
             }
             })}
         </div>
 
       </div>
      
       {/* End Body */}
     
     
     
     </div>
   );/* End return */
 } //End function
 
 
 //Resources
     //Rendering Components Inside a Loop
         //https://www.youtube.com/watch?v=8AY4siDB3ow
 